import { ChevronRightIcon } from 'lucide-react';
import type { JSX } from 'react';

/**
 * The parts the SharePoint and OneDrive pickers share.
 *
 * They are two halves of one Microsoft connection and one dialog, so a person moving between the
 * tabs should not have to learn a second way to read where they are or what a row's state means.
 * Keeping the path and the standing vocabulary here is what makes that true by construction rather
 * than by two teams remembering — the OneDrive half used to say "Back to OneDrive root" where the
 * SharePoint half drew a path, and had no way at all to say "you cannot add this yet".
 */

/** One step of the path. The LAST crumb is the level on screen and renders as plain text. */
export interface BrowseCrumb {
  key: string;
  label: string;
  onSelect: () => void;
}

/** The path from the tree's root down to the level on screen. */
export function PickerBreadcrumb({
  crumbs,
  label,
}: {
  crumbs: readonly BrowseCrumb[];
  /** What a screen reader calls this path. Names the tree, so two pickers do not both say "path". */
  label: string;
}): JSX.Element {
  const last = crumbs.length - 1;
  return (
    <nav
      className="flex flex-wrap items-center gap-1 text-sm"
      aria-label={label}
    >
      {crumbs.map((crumb, depth) => (
        <span key={crumb.key} className="flex items-center gap-1">
          {depth > 0 && (
            <ChevronRightIcon
              className="size-4 text-muted-foreground"
              aria-hidden
            />
          )}
          {depth === last ? (
            <span className="font-medium text-foreground">{crumb.label}</span>
          ) : (
            <button
              type="button"
              onClick={crumb.onSelect}
              className="text-muted-foreground hover:text-foreground hover:underline"
            >
              {crumb.label}
            </button>
          )}
        </span>
      ))}
    </nav>
  );
}

/**
 * Where one browsed row stands against what is already on the list.
 *
 * `free` is the only state with something to press. The other three are the reasons there is not,
 * and they are not decoration: the server refuses an overlapping grant in either direction, so an
 * Add on a row that is already covered — or that already holds a narrower grant inside it — is a
 * button that can only fail. Both pickers had shipped one of those.
 */
/** One row already on the list that stands in the way of adding a wider one. */
export interface BlockingRow {
  id: string;
  name: string;
}

export type RowStanding =
  | { kind: 'free' }
  | { kind: 'added' }
  | { kind: 'covered'; by: string }
  /**
   * Blocked by narrower rows already on the list. It carries WHICH rows, not merely how many,
   * because the way out is an offer rather than an instruction: a picker that knows the names can
   * show them and take them off on the person's say-so, and one that only knows a count can do
   * nothing but tell somebody to go and find them.
   */
  | { kind: 'contains'; blocking: readonly BlockingRow[] };
